import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { PRIMARY_NAV } from "./navigation";
import { COMPANY } from "../../data/company";
import Button from "../ui/Button";
import { Label } from "../ui/Type";
import { color, font, motion, space, type, zIndex } from "../../design/tokens";

/**
 * Site header.
 *
 * Every page opens with a full-bleed ink band, so the header sits transparent
 * over it at rest and flips to solid paper once you scroll past it. That's the
 * scroll state — a genuine change of ground, not a shadow appearing.
 *
 * Desktop dropdowns open on hover *and* on keyboard focus, and close on Escape
 * or on leaving the group. The mobile menu is a full-screen ink overlay with
 * its own layout rather than the desktop list stuffed into a drawer: large
 * serif targets, sub-lists as accordions, and the phone number and quote CTA
 * pinned where a thumb reaches them.
 */

const SCROLL_THRESHOLD = 24;

const SiteHeader = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);

  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const closeTimer = useRef(null);

  // ── Scroll state ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Close everything on navigation ───────────────────────────────────────
  useEffect(() => {
    setOpenPanel(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname]);

  // ── Escape closes whichever thing is open ────────────────────────────────
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setOpenPanel(null);
      if (mobileOpen) {
        setMobileOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  // ── Body scroll lock + focus handoff for the mobile overlay ──────────────
  useEffect(() => {
    if (!mobileOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // A short close delay stops the panel flickering shut when the pointer
  // crosses the gap between the trigger and the panel below it.
  const openWithCancel = useCallback((label) => {
    clearTimeout(closeTimer.current);
    setOpenPanel(label);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenPanel(null), 120);
  }, []);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const onInk = !scrolled && !mobileOpen;
  const textColor = onInk ? color.onInk : color.ink;

  return (
    <>
      <Box
        component="a"
        href="#main"
        sx={{
          position: "fixed",
          top: 8,
          left: 8,
          zIndex: zIndex.drawer + 1,
          padding: "12px 20px",
          backgroundColor: color.ink,
          color: color.paper,
          ...type.label,
          transform: "translateY(-160%)",
          transition: `transform ${motion.fast} ${motion.ease}`,
          "&:focus-visible": { transform: "translateY(0)" },
        }}
      >
        Skip to content
      </Box>

      <Box
        component="header"
        onMouseLeave={scheduleClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: zIndex.header,
          backgroundColor: onInk ? "transparent" : color.paper,
          borderBottom: `1px solid ${onInk ? "transparent" : color.line}`,
          backdropFilter: onInk ? "none" : "saturate(180%) blur(12px)",
          transition: `background-color ${motion.base} ${motion.ease}, border-color ${motion.base} ${motion.ease}, height ${motion.base} ${motion.ease}`,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: space.maxWidth,
            marginInline: "auto",
            paddingInline: space.gutter,
            height: { xs: 68, md: scrolled ? 72 : 92 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
            transition: `height ${motion.base} ${motion.ease}`,
          }}
        >
          <Logo
            tone={onInk ? "onInk" : "ink"}
            markHeight={scrolled ? 26 : 30}
          />

          {/* ── Desktop navigation ──────────────────────────────────────── */}
          <Box
            component="nav"
            aria-label="Primary"
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 0.5,
              height: "100%",
            }}
          >
            {PRIMARY_NAV.map((item) => {
              const active = isActive(item.to);
              const hasPanel = Boolean(item.panel);
              const panelOpen = openPanel === item.label;

              return (
                <Box
                  key={item.label}
                  onMouseEnter={() => (hasPanel ? openWithCancel(item.label) : scheduleClose())}
                  onFocus={() => (hasPanel ? openWithCancel(item.label) : setOpenPanel(null))}
                  sx={{ display: "flex", alignItems: "center", height: "100%" }}
                >
                  <Box
                    component={Link}
                    to={item.to}
                    aria-expanded={hasPanel ? panelOpen : undefined}
                    aria-haspopup={hasPanel ? "true" : undefined}
                    sx={{
                      position: "relative",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      paddingInline: 1.75,
                      paddingBlock: 1.25,
                      fontFamily: font.body,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      letterSpacing: "0.01em",
                      color: textColor,
                      opacity: active ? 1 : 0.82,
                      transition: `opacity ${motion.base} ${motion.ease}, color ${motion.base} ${motion.ease}`,
                      "&:hover": { opacity: 1 },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 14,
                        right: 14,
                        bottom: 4,
                        height: "1px",
                        backgroundColor: onInk ? color.clayLight : color.clay,
                        transform: active || panelOpen ? "scaleX(1)" : "scaleX(0)",
                        transformOrigin: "left",
                        transition: `transform ${motion.base} ${motion.ease}`,
                      },
                      "&:hover::after": { transform: "scaleX(1)" },
                    }}
                  >
                    {item.label}
                    {hasPanel ? (
                      <Box
                        component="svg"
                        aria-hidden="true"
                        viewBox="0 0 10 6"
                        sx={{
                          width: 9,
                          height: 6,
                          fill: "none",
                          stroke: "currentColor",
                          strokeWidth: 1.4,
                          opacity: 0.7,
                          transform: panelOpen ? "rotate(180deg)" : "none",
                          transition: `transform ${motion.base} ${motion.ease}`,
                        }}
                      >
                        <path d="M1 1l4 4 4-4" />
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* ── Right rail ──────────────────────────────────────────────── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2.5 } }}>
            <Box
              component="a"
              href={`tel:${COMPANY.phone.primaryE164}`}
              sx={{
                display: { xs: "none", xl: "inline-flex" },
                alignItems: "center",
                gap: 1,
                fontFamily: font.body,
                fontWeight: 600,
                fontSize: "0.875rem",
                color: textColor,
                opacity: 0.82,
                transition: `opacity ${motion.base} ${motion.ease}`,
                "&:hover": { opacity: 1 },
              }}
            >
              {COMPANY.phone.primary}
            </Box>

            <Button
              to="/contact"
              variant={onInk ? "onInk" : "primary"}
              size="sm"
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              Get a Free Quote
            </Button>

            <Box
              component="button"
              type="button"
              ref={menuButtonRef}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              sx={{
                display: { xs: "inline-flex", lg: "none" },
                flexDirection: "column",
                justifyContent: "center",
                gap: "5px",
                width: 44,
                height: 44,
                marginRight: "-10px",
                padding: 0,
                border: "none",
                background: "none",
                cursor: "pointer",
                color: textColor,
              }}
            >
              <Box sx={{ width: 22, height: "1.5px", backgroundColor: "currentColor" }} />
              <Box sx={{ width: 22, height: "1.5px", backgroundColor: "currentColor" }} />
            </Box>
          </Box>
        </Box>

        {/* ── Desktop mega panel ────────────────────────────────────────── */}
        {PRIMARY_NAV.filter((item) => item.panel).map((item) => {
          const panelOpen = openPanel === item.label;
          const isServices = item.panel.items.length > 5;

          return (
            <Box
              key={`${item.label}-panel`}
              hidden={!panelOpen}
              onMouseEnter={() => openWithCancel(item.label)}
              sx={{
                display: { xs: "none", lg: panelOpen ? "block" : "none" },
                position: "absolute",
                left: 0,
                right: 0,
                top: "100%",
                backgroundColor: color.paper,
                borderTop: `1px solid ${color.line}`,
                borderBottom: `1px solid ${color.line}`,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: space.maxWidth,
                  marginInline: "auto",
                  paddingInline: space.gutter,
                  paddingBlock: 5,
                  display: "grid",
                  gridTemplateColumns: "minmax(220px, 1fr) 2.4fr",
                  gap: 6,
                }}
              >
                <Box>
                  <Label>{item.label}</Label>
                  <Box
                    component="p"
                    sx={{ ...type.bodySm, color: color.graphite, marginTop: 2, maxWidth: 260 }}
                  >
                    {item.panel.intro}
                  </Box>
                </Box>

                <Box
                  component="ul"
                  sx={{
                    listStyle: "none",
                    margin: 0,
                    padding: 0,
                    display: "grid",
                    gridTemplateColumns: isServices ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
                    columnGap: 4,
                    rowGap: 0.5,
                  }}
                >
                  {item.panel.items.map((child) => (
                    <Box component="li" key={child.to}>
                      <Box
                        component={Link}
                        to={child.to}
                        sx={{
                          display: "block",
                          paddingBlock: 1.25,
                          borderBottom: `1px solid ${color.lineSoft}`,
                          fontFamily: font.body,
                          fontWeight: 600,
                          fontSize: "0.9375rem",
                          color: color.ink,
                          transition: `color ${motion.base} ${motion.ease}, padding-left ${motion.base} ${motion.ease}`,
                          "&:hover": { color: color.clay, paddingLeft: "6px" },
                        }}
                      >
                        {child.label}
                      </Box>
                    </Box>
                  ))}
                  <Box component="li" sx={{ gridColumn: "1 / -1", marginTop: 2 }}>
                    <Box
                      component={Link}
                      to={item.panel.footerLink.to}
                      sx={{
                        ...type.label,
                        color: color.clay,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {item.panel.footerLink.label} →
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ── Mobile overlay ─────────────────────────────────────────────── */}
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        hidden={!mobileOpen}
        sx={{
          display: mobileOpen ? "flex" : "none",
          position: "fixed",
          inset: 0,
          zIndex: zIndex.drawer,
          backgroundColor: color.ink,
          flexDirection: "column",
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingInline: space.gutter,
            height: 68,
            flexShrink: 0,
            borderBottom: `1px solid ${color.lineOnInkSoft}`,
          }}
        >
          <Logo tone="onInk" markHeight={26} />
          <Box
            component="button"
            type="button"
            ref={closeButtonRef}
            onClick={() => {
              setMobileOpen(false);
              menuButtonRef.current?.focus();
            }}
            aria-label="Close menu"
            sx={{
              width: 44,
              height: 44,
              marginRight: "-10px",
              padding: 0,
              border: "none",
              background: "none",
              color: color.onInk,
              cursor: "pointer",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 20 20"
              aria-hidden="true"
              sx={{ width: 20, height: 20, stroke: "currentColor", strokeWidth: 1.4, fill: "none" }}
            >
              <path d="M4 4l12 12M16 4L4 16" />
            </Box>
          </Box>
        </Box>

        <Box
          component="nav"
          aria-label="Mobile"
          sx={{ paddingInline: space.gutter, paddingBlock: 3, flex: "1 0 auto" }}
        >
          {PRIMARY_NAV.map((item) => {
            const expanded = mobileSection === item.label;

            return (
              <Box
                key={item.label}
                sx={{ borderBottom: `1px solid ${color.lineOnInkSoft}` }}
              >
                <Box sx={{ display: "flex", alignItems: "stretch" }}>
                  <Box
                    component={Link}
                    to={item.to}
                    sx={{
                      flex: 1,
                      paddingBlock: 2.5,
                      fontFamily: font.display,
                      fontSize: "2rem",
                      lineHeight: 1.1,
                      color: isActive(item.to) ? color.clayLight : color.onInk,
                    }}
                  >
                    {item.label}
                  </Box>

                  {item.panel ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setMobileSection(expanded ? null : item.label)}
                      aria-label={`${expanded ? "Collapse" : "Expand"} ${item.label}`}
                      aria-expanded={expanded}
                      sx={{
                        width: 56,
                        border: "none",
                        background: "none",
                        color: color.onInkMute,
                        cursor: "pointer",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Box
                        component="svg"
                        viewBox="0 0 14 14"
                        aria-hidden="true"
                        sx={{
                          width: 14,
                          height: 14,
                          stroke: "currentColor",
                          strokeWidth: 1.3,
                          fill: "none",
                          transform: expanded ? "rotate(45deg)" : "none",
                          transition: `transform ${motion.base} ${motion.ease}`,
                        }}
                      >
                        <path d="M7 1v12M1 7h12" />
                      </Box>
                    </Box>
                  ) : null}
                </Box>

                {item.panel && expanded ? (
                  <Box
                    component="ul"
                    sx={{ listStyle: "none", margin: 0, padding: 0, paddingBottom: 2.5 }}
                  >
                    {item.panel.items.map((child) => (
                      <Box component="li" key={child.to}>
                        <Box
                          component={Link}
                          to={child.to}
                          sx={{
                            display: "block",
                            paddingBlock: 1.25,
                            ...type.bodySm,
                            fontWeight: 600,
                            color: color.onInkMute,
                          }}
                        >
                          {child.label}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            paddingInline: space.gutter,
            paddingBottom: 5,
            paddingTop: 2,
            flexShrink: 0,
            display: "grid",
            gap: 2.5,
          }}
        >
          <Button to="/contact" variant="onInk" size="lg" fullWidth>
            Get a Free Quote
          </Button>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Box
              component="a"
              href={`tel:${COMPANY.phone.primaryE164}`}
              sx={{ ...type.body, color: color.onInk, fontWeight: 600 }}
            >
              {COMPANY.phone.primary}
            </Box>
            <Box
              component="a"
              href={`mailto:${COMPANY.email}`}
              sx={{ ...type.bodySm, color: color.onInkMute, wordBreak: "break-word" }}
            >
              {COMPANY.email}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SiteHeader;
